import { Allocation, OptimizeResult, Problem, Step } from './types';
import { cloneAlloc, countBasic, totalCost } from './utils';

interface Cell {
  r: number;
  c: number;
}

const inPath = (path: Cell[], c: Cell) => path.some((p) => p.r === c.r && p.c === c.c);

// Busca un ciclo cerrado que parta de la celda `start` (que entra a la base)
// usando como esquinas las celdas básicas. El ciclo alterna movimientos
// horizontales y verticales.
function findLoop(start: Cell, basicCells: Cell[]): Cell[] | null {
  const candidates = basicCells.filter((c) => !(c.r === start.r && c.c === start.c));
  const path: Cell[] = [start];

  const dfs = (orientation: 'row' | 'col' | null): Cell[] | null => {
    const cur = path[path.length - 1];

    if (path.length >= 4) {
      const closeRow = cur.r === start.r && orientation === 'col';
      const closeCol = cur.c === start.c && orientation === 'row';
      if (closeRow || closeCol) return path.slice();
    }

    for (const next of candidates) {
      if (inPath(path, next)) continue;
      const sameRow = next.r === cur.r;
      const sameCol = next.c === cur.c;
      if (!sameRow && !sameCol) continue;
      if (sameRow && orientation === 'row') continue;
      if (sameCol && orientation === 'col') continue;
      path.push(next);
      const res = dfs(sameRow ? 'row' : 'col');
      if (res) return res;
      path.pop();
    }
    return null;
  };

  return dfs(null);
}

// Lista las celdas básicas (asignación no nula) de la matriz.
function basicCellsOf(alloc: Allocation): Cell[] {
  const cells: Cell[] = [];
  for (let i = 0; i < alloc.length; i++)
    for (let j = 0; j < alloc[i].length; j++) if (alloc[i][j] !== null) cells.push({ r: i, c: j });
  return cells;
}

// Corrige la degeneración: si hay menos de m+n-1 celdas básicas, agrega
// asignaciones cero en posiciones que no formen ciclo (independientes).
function fixDegeneracy(alloc: Allocation): void {
  const m = alloc.length;
  const n = alloc[0].length;
  const needed = m + n - 1;

  while (countBasic(alloc) < needed) {
    let added = false;
    for (let i = 0; i < m && !added; i++) {
      for (let j = 0; j < n && !added; j++) {
        if (alloc[i][j] !== null) continue;
        const basics = basicCellsOf(alloc);
        // Si agregar esta celda no crea un ciclo, es independiente: la usamos.
        if (findLoop({ r: i, c: j }, basics) === null) {
          alloc[i][j] = 0;
          added = true;
        }
      }
    }
    if (!added) break; // salvaguarda
  }
}

// Calcula los multiplicadores u (filas) y v (columnas) resolviendo
// u_i + v_j = c_ij para cada celda básica, fijando u_0 = 0.
// Devuelve además las ecuaciones (en orden de derivación) para mostrarlas.
function computeUV(
  alloc: Allocation,
  costs: number[][],
  p: Problem
): { u: (number | null)[]; v: (number | null)[]; equations: string[] } {
  const m = alloc.length;
  const n = alloc[0].length;
  const u: (number | null)[] = new Array(m).fill(null);
  const v: (number | null)[] = new Array(n).fill(null);
  u[0] = 0;
  const equations: string[] = [`u(${p.sources[0]}) = 0   (referencia)`];

  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < m; i++) {
      for (let j = 0; j < n; j++) {
        if (alloc[i][j] === null) continue;
        if (u[i] !== null && v[j] === null) {
          v[j] = costs[i][j] - (u[i] as number);
          equations.push(
            `v(${p.dests[j]}) = c(${p.sources[i]},${p.dests[j]}) − u(${p.sources[i]}) = ${costs[i][j]} − ${u[i]} = ${v[j]}`
          );
          changed = true;
        } else if (u[i] === null && v[j] !== null) {
          u[i] = costs[i][j] - (v[j] as number);
          equations.push(
            `u(${p.sources[i]}) = c(${p.sources[i]},${p.dests[j]}) − v(${p.dests[j]}) = ${costs[i][j]} − ${v[j]} = ${u[i]}`
          );
          changed = true;
        }
      }
    }
  }
  return { u, v, equations };
}

// Optimiza una solución inicial factible con el método MODI (u-v).
export function optimize(p: Problem, initial: Allocation): OptimizeResult {
  const m = p.supply.length;
  const n = p.demand.length;
  const alloc = cloneAlloc(initial);
  const steps: Step[] = [];

  fixDegeneracy(alloc);

  let iterations = 0;
  const MAX_ITER = 100;
  let optimal = false;

  while (iterations < MAX_ITER) {
    const { u, v, equations } = computeUV(alloc, p.costs, p);

    // Costos reducidos de las celdas no básicas: d_ij = c_ij - u_i - v_j
    const improvements: { row: number; col: number; delta: number }[] = [];
    const dEquations: string[] = [];
    for (let i = 0; i < m; i++) {
      for (let j = 0; j < n; j++) {
        if (alloc[i][j] !== null) continue;
        if (u[i] === null || v[j] === null) continue;
        const delta = p.costs[i][j] - (u[i] as number) - (v[j] as number);
        improvements.push({ row: i, col: j, delta });
        dEquations.push(
          `d(${p.sources[i]},${p.dests[j]}) = ${p.costs[i][j]} − (${u[i]}) − (${v[j]}) = ${delta}`
        );
      }
    }
    const allEquations = [
      'Multiplicadores (u + v = c en celdas básicas):',
      ...equations,
      'Costos reducidos de celdas no básicas (d = c − u − v):',
      ...dEquations,
    ];

    const negatives = improvements.filter((x) => x.delta < 0);

    if (negatives.length === 0) {
      optimal = true;
      steps.push({
        title: `Iteración ${iterations + 1}: óptimo`,
        description:
          'Todos los costos reducidos (cij − ui − vj) de las celdas no básicas son ≥ 0. La solución actual es óptima.',
        alloc: cloneAlloc(alloc),
        remainingSupply: p.supply.slice(),
        remainingDemand: p.demand.slice(),
        u: u.slice(),
        v: v.slice(),
        improvements,
        equations: allEquations,
      });
      break;
    }

    // Celda entrante: el costo reducido más negativo.
    negatives.sort((a, b) => a.delta - b.delta);
    const entering = negatives[0];
    const loop = findLoop({ r: entering.row, c: entering.col }, basicCellsOf(alloc));

    if (!loop) {
      // No debería ocurrir si la degeneración está bien tratada.
      steps.push({
        title: `Iteración ${iterations + 1}: error`,
        description:
          'No se pudo construir un ciclo cerrado para la celda entrante. Se detiene la optimización.',
        alloc: cloneAlloc(alloc),
        remainingSupply: p.supply.slice(),
        remainingDemand: p.demand.slice(),
        u: u.slice(),
        v: v.slice(),
        improvements,
        equations: allEquations,
      });
      break;
    }

    // En el ciclo, las posiciones pares (+) reciben y las impares (−) ceden.
    // theta = mínima asignación entre las posiciones (−).
    let theta = Infinity;
    let leaving: Cell | null = null;
    for (let k = 1; k < loop.length; k += 2) {
      const cell = loop[k];
      const val = alloc[cell.r][cell.c] as number;
      if (val < theta) {
        theta = val;
        leaving = cell;
      }
    }

    steps.push({
      title: `Iteración ${iterations + 1}`,
      description:
        `Celda entrante (${p.sources[entering.row]}, ${p.dests[entering.col]}) con costo reducido ${entering.delta} (el más negativo). ` +
        `Se forma un ciclo cerrado y se transfieren θ = ${theta} unidades. ` +
        `Sale de la base la celda (${p.sources[(leaving as Cell).r]}, ${p.dests[(leaving as Cell).c]}).`,
      alloc: cloneAlloc(alloc),
      remainingSupply: p.supply.slice(),
      remainingDemand: p.demand.slice(),
      u: u.slice(),
      v: v.slice(),
      improvements,
      equations: allEquations,
      loop: loop.slice(),
      highlight: { row: entering.row, col: entering.col },
    });

    // Aplicar el ajuste: +theta en pares, -theta en impares.
    for (let k = 0; k < loop.length; k++) {
      const cell = loop[k];
      const cur = alloc[cell.r][cell.c] ?? 0;
      alloc[cell.r][cell.c] = k % 2 === 0 ? cur + theta : cur - theta;
    }
    // La celda saliente abandona la base.
    if (leaving) alloc[leaving.r][leaving.c] = null;

    // Reparar degeneración para la siguiente iteración.
    fixDegeneracy(alloc);

    iterations++;
  }

  const cost = totalCost(alloc, p.costs);
  steps.push({
    title: 'Solución óptima',
    description: optimal
      ? `Costo total mínimo = ${cost}.`
      : `Se alcanzó el límite de iteraciones. Mejor costo encontrado = ${cost}.`,
    alloc: cloneAlloc(alloc),
    remainingSupply: p.supply.slice(),
    remainingDemand: p.demand.slice(),
  });

  return { alloc, steps, totalCost: cost, optimal, iterations };
}
